import clsx from 'clsx';
import A from 'components/atoms/A/A';
import Alert from 'components/atoms/Alert/Alert';
import LoadingOverlay from 'components/atoms/LoadingOverlay/LoadingOverlay';
import RegistrationCard from 'components/atoms/RegistrationCard/RegistrationCard';
import UsernameInput from 'components/atoms/TextInput/UsernameInput';
import TurnstileWidget from 'components/atoms/TurnstileWidget/TurnstileWidget';
import Txt from 'components/atoms/Txt/Txt';
import Form from 'components/molecules/Form/Form';
import { submitResetPasswordRequest } from 'components/templates/AuthenticationProvider/authApiUtils';
import { SecondFactorRequiredError } from 'components/templates/AuthenticationProvider/SecondFactorRequiredError';
import usePanelConfiguration from 'hooks/usePanelConfiguration';
import usePromiseState from 'hooks/usePromiseState';
import useRecaptcha, { RecaptchaProvider } from 'hooks/useRecaptcha';
import { useRouter } from 'next/router';
import React from 'react';
import classes from './ForgotPassword.module.scss';

export interface Props {}

function ForgotPassword() {
  const router = useRouter();
  const usernameRef = React.useRef<HTMLInputElement>(null);
  const [loginError, setLoginError] = React.useState<string | undefined>(undefined);
  const [handlePasswordResetRequest, resetPasswordRequestState] = usePromiseState(
    submitResetPasswordRequest,
  );
  const [successMessage, setSuccessMessage] = React.useState<string | undefined>();
  const panelConfiguration = usePanelConfiguration();
  const { loadRecaptcha, isLoadingRecaptcha } = useRecaptcha('forgot_password');

  const [turnstileToken, setTurnstileToken] = React.useState<string | null>(null);
  const handleTurnstileTokenReceived = React.useCallback(
    (token: string) => {
      setTurnstileToken(token);
    },
    [setTurnstileToken],
  );

  const handleSubmit = async (formData: FormData) => {
    setLoginError(undefined);
    if (usernameRef.current?.value) {
      try {
        const username = usernameRef.current?.value.trim().toLocaleLowerCase();
        const overrideCountry = formData.get('overrideCountry')?.toString();

        const recaptchaData = await loadRecaptcha();
        const response = await handlePasswordResetRequest(
          {
            locale: router.locale,
            recaptchaData: recaptchaData,
            turnstileToken: turnstileToken || undefined,
          },
          username,
          overrideCountry,
        );
        setSuccessMessage(response.message);
      } catch (err: any) {
        if (err instanceof SecondFactorRequiredError) {
          await router.push(
            `/reset-password?session_id=${err.getSessionId()}&username=${encodeURIComponent(
              usernameRef.current?.value,
            )}`,
            `/reset-password`,
          );
        } else {
          setLoginError(
            err.updateError ||
              'You have been temporarily blocked for security reasons for up to 72 hours, please try resetting your password later',
          );
        }
      }
    }
  };

  let introMessage;

  if (
    panelConfiguration?.registrationOptions.email &&
    panelConfiguration?.registrationOptions.phoneNumber
  ) {
    introMessage =
      'Enter your email address or mobile number you use to sign in and we’ll send you a link to reset your password.';
  } else if (panelConfiguration?.registrationOptions.email) {
    introMessage =
      'Enter your email address you use to sign in and we’ll send you a link to reset your password.';
  } else if (panelConfiguration?.registrationOptions.phoneNumber) {
    introMessage =
      'Enter the mobile number you use to sign in and we’ll send you a one time code to reset your password.';
  }

  return (
    <RegistrationCard
      title={successMessage ? 'Check your messages' : 'Forgot Password?'}
      narrowContainer
    >
      {(resetPasswordRequestState.isLoading || isLoadingRecaptcha) && <LoadingOverlay />}
      {successMessage ? (
        <>
          <Txt className={classes.successMessage}>{successMessage}</Txt>
        </>
      ) : (
        <>
          <Txt className={clsx(classes.instructions)} component={'p'}>
            {introMessage}
          </Txt>
          {loginError && (
            <Alert
              title={'Please try again'}
              subtitle={loginError}
              type={'error'}
              onClose={() => {
                setLoginError(undefined);
              }}
            />
          )}
          <Form
            className={classes.form}
            submitDisabled={
              resetPasswordRequestState.isLoading ||
              isLoadingRecaptcha ||
              (Boolean(process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY) && !turnstileToken)
            }
            submitName={'Reset password'}
            buttonWrapperClassName={classes.buttonWrapper}
            onSubmit={handleSubmit}
            submitButtonType="primary"
            submitButtonClassName={classes.submitButton}
          >
            <UsernameInput ref={usernameRef} />
            <TurnstileWidget
              onTokenReceived={handleTurnstileTokenReceived}
              action="reset-password"
            />
          </Form>

          <Txt className={classes.alternativeCta}>
            New to Mobiworkx? <A href="/">Create account</A>
          </Txt>
        </>
      )}
    </RegistrationCard>
  );
}

export default function ForgotPasswordWrapper(props: Props) {
  return (
    <RecaptchaProvider>
      <ForgotPassword {...props} />
    </RecaptchaProvider>
  );
}
